import type {ByodFilterMap} from 'src/types/byod';


export type InjectedFilterMap = ByodFilterMap;

/**
 * Filter keys that ALWAYS apply to every widget, regardless of its
 * `supported_filters`. The date window scopes the whole dashboard, so it's
 * never projected out.
 */
export const ALWAYS_APPLIED_FILTER_KEYS = new Set(['start_date', 'end_date']);

/**
 * Project the host-injected filters down to what a single widget actually
 * understands — BYOD's "smart filter matching." A widget receives an injected
 * filter only if it's a date key (always applies) or the key is in the widget's
 * `supported_filters`. Everything else is dropped, so a chart that doesn't deal
 * with (say) `job_id` simply ignores it instead of sending a filter the widget
 * can't honor.
 *
 * `supportedFilters === undefined` means "unknown" (e.g. a just-added builder
 * widget with no catalog def loaded yet) — in that case nothing is projected
 * and all injected filters pass through, preserving prior behavior. An empty
 * array means "supports no filters," so only the date keys pass.
 */
export function projectInjectedFilters(
  injected: InjectedFilterMap,
  supportedFilters: string[] | undefined,
): InjectedFilterMap {
  if (supportedFilters === undefined) {
    return injected;
  }
  const supported = new Set(supportedFilters);
  const out: InjectedFilterMap = {};
  for (const [key, value] of Object.entries(injected)) {
    if (ALWAYS_APPLIED_FILTER_KEYS.has(key) || supported.has(key)) {
      out[key] = value;
    }
  }
  return out;
}
